import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import RespondPanel, { RespondState } from '../components/interview/RespondPanel'

// Phase 2 member Home. Any initiative with active prompts renders the full
// respond interface inline (RespondPanel, same component as the /respond
// page): unanswered ones open the stepper so members can answer without
// clicking through; fully-answered ones show the "all caught up" summary
// with a per-response Edit button. Only prompt-less initiatives fall back to
// the compact card grid below.

interface Initiative {
  id: number
  slug: string
  name: string
  description: string | null
}

interface ActivePromptRow {
  initiative: Initiative
  // First active prompt, for the compact card display.
  active_prompt: {
    id: number
    question_text: string
    activation_id: number
  } | null
  active_count: number
  // True when every active prompt has a current answer from this user.
  all_answered: boolean
  // Full respond state, handed to RespondPanel so the inline stepper renders
  // instantly instead of re-fetching what we just loaded.
  respond_state: RespondState | null
}

// We can derive the same view client-side from /api/v1/initiatives + a hit to
// each initiative's respond endpoint, but rolling one endpoint per initiative
// is wasteful. For now we get the list from /api/v1/initiatives and the
// respond state lazily per card (a Phase 2 follow-up could collapse this into
// a single /api/v1/me/home call).

export default function Home() {
  const currentPerson = useAuthStore((s) => s.currentPerson)
  const isAdmin = useAuthStore((s) => s.isAdmin())
  const [rows, setRows] = useState<ActivePromptRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const initiatives: Initiative[] = await api.get('/api/v1/initiatives')
        const enriched: ActivePromptRow[] = await Promise.all(
          initiatives.map(async (initiative) => {
            try {
              const state: RespondState = await api.get(
                `/api/v1/initiatives/${initiative.slug}/respond`,
              )
              const first = state.activations[0]
              return {
                initiative,
                active_prompt: first
                  ? {
                      id: first.prompt.id,
                      question_text: first.prompt.question_text,
                      activation_id: first.id,
                    }
                  : null,
                active_count: state.activations.length,
                all_answered:
                  state.activations.length > 0 &&
                  state.activations.every((a) => a.current_response),
                respond_state: state,
              }
            } catch {
              return {
                initiative,
                active_prompt: null,
                active_count: 0,
                all_answered: false,
                respond_state: null,
              }
            }
          }),
        )

        if (!cancelled) setRows(enriched)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load initiatives')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  // Every initiative that has active prompts renders the full RespondPanel:
  // unanswered ones open the stepper, fully-answered ones show the "all
  // caught up" summary with per-response Edit buttons (same as the
  // /initiatives/:slug/respond page). Unanswered come first since they're
  // the actionable ones. Only initiatives with NO active prompt stay compact
  // cards.
  const withPrompts = rows
    .filter((r) => r.active_count > 0)
    .sort((a, b) => Number(a.all_answered) - Number(b.all_answered))
  const noPrompts = rows.filter((r) => r.active_count === 0)
  return (
    <div className="space-y-6">
      {/* Welcome header renders unconditionally — the inline respond panels
          below use the initiative name as their heading, so there's no
          duplication and no header swap/flash while loading. */}
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
          Welcome{currentPerson?.full_name ? `, ${currentPerson.full_name}` : ''}
        </h2>
        {/*<p className="mt-1 text-sm text-slate-500">
          Share what your agency knows, and surface what the Collective has learned.
        </p>*/}
      </div>

      {loading && (
        <div className="animate-pulse" aria-label="Loading your initiatives">
          <div className="h-40 max-w-3xl rounded-lg border border-slate-200 bg-white p-6">
            <div className="h-3 w-1/3 rounded bg-slate-100" />
            <div className="mt-4 h-3 w-2/3 rounded bg-slate-100" />
            <div className="mt-2 h-3 w-1/2 rounded bg-slate-100" />
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-6 text-sm text-slate-500">
          You have not been assigned to any initiatives yet. An admin needs to add you.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          {withPrompts.length > 0 && (
            <div className="space-y-10">
              {withPrompts.map(({ initiative, respond_state }) => (
                <RespondPanel
                  key={initiative.id}
                  slug={initiative.slug}
                  initialState={respond_state ?? undefined}
                />
              ))}
            </div>
          )}

          {noPrompts.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {noPrompts.map(({ initiative, active_prompt, all_answered }) => (
                <InitiativeCard
                  key={initiative.id}
                  initiative={initiative}
                  activePrompt={active_prompt}
                  hasCurrentResponse={all_answered}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface InitiativeCardProps {
  initiative: Initiative
  activePrompt: ActivePromptRow['active_prompt']
  hasCurrentResponse: boolean
  isAdmin: boolean
}

function InitiativeCard({
  initiative,
  activePrompt,
  hasCurrentResponse,
  isAdmin,
}: InitiativeCardProps) {
  const truncatedPrompt =
    activePrompt && activePrompt.question_text.length > 140
      ? activePrompt.question_text.slice(0, 137) + '…'
      : activePrompt?.question_text

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900 truncate">{initiative.name}</h3>
          {initiative.description && (
            <p className="mt-1 text-xs text-slate-500 line-clamp-2">{initiative.description}</p>
          )}
        </div>
        {hasCurrentResponse && (
          <span className="shrink-0 inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
            Answered
          </span>
        )}
      </div>

      <div className="mt-4 rounded-md bg-slate-50 p-3">
        {activePrompt ? (
          <p className="text-sm text-slate-700">{truncatedPrompt}</p>
        ) : (
          <p className="text-xs text-slate-400 italic">No active prompt right now.</p>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        {activePrompt && (
          <Link
            to={`/initiatives/${initiative.slug}/respond`}
            className="inline-flex items-center rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-500"
          >
            {hasCurrentResponse ? 'Edit your answer' : 'Answer'}
          </Link>
        )}
        {isAdmin && (
          <Link
            to={`/admin/initiatives/${initiative.slug}/queue`}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Manage queue →
          </Link>
        )}
        {isAdmin && (
          <Link
            to={`/admin/initiatives/${initiative.slug}/intersections`}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Intersections →
          </Link>
        )}
      </div>
    </div>
  )
}
